/**
 * src/components/admin/AdminPromos.js
 *
 * Tab de gestión de promociones en el panel admin.
 */

import { define } from '@components/Component.js'
import { h } from '@core/dom.js'
import { showToast, nextId, toInputDate } from './adminHelpers.js'
import { uploadButton } from './adminUpload.js'

const PROMO_TAGS = ['Descuento', 'Novedad', '2×1', 'Lanzamiento', 'Gratis']

/* ── Formulario de promo ─────────────────────────────────── */
function buildForm(promo = {}, businesses = []) {
    const def = {
        businessId: '', business: '', image: '',
        tag: 'Descuento', title: '', description: '',
        expires: '', enabled: true, responsable: '',
        facebook: '', instagram: '', tiktok: '', whatsapp: '',
        ...promo
    }

    const f = (tag, attrs, ...ch) => h(tag, attrs, ...ch)

    const field = (label, input, hint) => f('div', { className: 'adm-field' },
        f('label', { className: 'adm-label' }, label),
        input,
        ...(hint ? [f('p', { className: 'adm-hint' }, hint)] : [])
    )
    const inp = (name, opts = {}) => f('input', {
        className: 'adm-input', name,
        value: opts.value ?? def[name] ?? '',
        type: opts.type ?? 'text',
        placeholder: opts.placeholder ?? ''
    })
    const sel = (name, options, value) => {
        const s = f('select', { className: 'adm-select', name })
        options.forEach(([v, l]) => {
            const o = f('option', { value: v }, l)
            if (v === String(value)) o.selected = true
            s.appendChild(o)
        })
        return s
    }

    return f('form', { className: 'adm-form', id: 'promo-form' },
        f('div', { className: 'adm-form-grid' },
            field('Título de la promoción', inp('title', { placeholder: 'Ej: 20% en pastas artesanales' })),
            field('Tipo / Tag', sel('tag', PROMO_TAGS.map(t => [t, t]), def.tag)),
            field('Negocio vinculado', (() => {
                // Campo oculto con el nombre del negocio (lo llena el selector)
                const hiddenName = f('input', { type: 'hidden', name: 'business', value: def.business || '' })

                if (!businesses.length) {
                    // Sin lista disponible: campos de texto manuales como respaldo
                    return f('span',
                        inp('business', { placeholder: 'Nombre del negocio', value: def.business }),
                        inp('businessId', { type: 'number', placeholder: 'ID', value: def.businessId })
                    )
                }

                const bizSel = f('select', { className: 'adm-select', name: 'businessId' })
                const ph = f('option', { value: '' }, '— Selecciona un negocio —')
                if (!def.businessId) ph.selected = true
                bizSel.appendChild(ph)
                businesses.forEach(b => {
                    const o = f('option', { value: String(b.id) }, `#${b.id} — ${b.name}`)
                    o.dataset.name = b.name
                    if (String(b.id) === String(def.businessId)) o.selected = true
                    bizSel.appendChild(o)
                })
                bizSel.addEventListener('change', () => {
                    const opt = bizSel.selectedOptions[0]
                    hiddenName.value = opt ? (opt.dataset.name || '') : ''
                })
                const wrap = f('span')
                wrap.appendChild(bizSel)
                wrap.appendChild(hiddenName)
                return wrap
            })(), 'Elige el negocio al que pertenece esta promo'),
            field('Responsable del anuncio', inp('responsable', { placeholder: 'Ej: María Pérez', value: def.responsable || '' }),
                'Solo visible en este panel'),
        ),
        field('Descripción', f('textarea', {
            className: 'adm-textarea', name: 'description', rows: 3
        }, def.description || ''), 'Aparece en la página de detalle de la promo'),
        f('div', { className: 'adm-form-grid' },
            field('Imagen', uploadButton(inp('image', { placeholder: 'URL o clic en 📤 Subir', value: def.image })),
                'Clic en 📤 para subir la imagen desde tu computador'),
            field('Fecha de vencimiento', inp('expires', { type: 'date', value: toInputDate(def.expires) }),
                'Déjalo vacío si no vence'),
        ),
        f('div', { className: 'adm-schedule' },
            f('div', { className: 'adm-schedule-title' }, '🔗 Redes sociales'),
            f('div', { className: 'adm-form-grid' },
                field('Facebook', inp('facebook', { placeholder: 'https://facebook.com/...', value: def.facebook })),
                field('Instagram', inp('instagram', { placeholder: 'https://instagram.com/...', value: def.instagram })),
                field('TikTok', inp('tiktok', { placeholder: 'https://tiktok.com/@...', value: def.tiktok })),
                field('WhatsApp', inp('whatsapp', { placeholder: 'Ej: 573001234567', value: def.whatsapp })),
            )
        ),
        f('div', { className: 'adm-schedule' },
            f('div', { className: 'adm-schedule-title' }, '🗓 Visibilidad'),
            field('Estado', sel('enabled', [['true','✅ Habilitada'],['false','❌ Deshabilitada']], String(def.enabled)))
        )
    )
}

function readForm(form) {
    const g = name => form.querySelector(`[name="${name}"]`)?.value?.trim() ?? ''
    return {
        business:    g('business'),
        businessId:  Number(g('businessId')) || null,
        responsable: g('responsable') || null,
        image:       g('image'),
        tag:         g('tag'),
        title:       g('title'),
        description: form.querySelector('[name=description]')?.value?.trim() ?? '',
        expires:     g('expires') || null,
        enabled:     g('enabled') === 'true',
        facebook:    g('facebook') || null,
        instagram:   g('instagram') || null,
        tiktok:      g('tiktok') || null,
        whatsapp:    g('whatsapp') || null,
    }
}

/* ── Componente principal ────────────────────────────────── */
define('AdminPromos', ({ promos, businesses = [], onSave, onDelete, onToggle }) => {

    const TAG_COLORS = {
        'Descuento':   '#dcfce7', 'Novedad': '#dbeafe', '2×1': '#fef9c3',
        'Lanzamiento': '#f3e8ff', 'Gratis':  '#ffe4e6'
    }

    function openModal(promoData) {
        const isEdit = !!promoData

        // Referencia directa al formulario para evitar querySelector('#id') que puede fallar
        const form = buildForm(promoData || {}, businesses)
        form.addEventListener('submit', e => e.preventDefault())

        const closeBtn  = h('button', { className: 'adm-btn adm-btn--ghost adm-btn--sm adm-btn--icon', type: 'button' }, '✕')
        const cancelBtn = h('button', { className: 'adm-btn adm-btn--ghost', type: 'button' }, 'Cancelar')
        const saveBtn   = h('button', { className: 'adm-btn adm-btn--primary', type: 'button' },
            isEdit ? '💾 Guardar cambios' : '✅ Crear promo'
        )

        const overlay = h('div', { className: 'adm-modal-overlay' },
            h('div', { className: 'adm-modal' },
                h('div', { className: 'adm-modal__head' },
                    h('span', { className: 'adm-modal__title' },
                        isEdit ? `✏️ Editar: ${promoData.title}` : '➕ Nueva promoción'
                    ),
                    closeBtn
                ),
                h('div', { className: 'adm-modal__body' }, form),
                h('div', { className: 'adm-modal__foot' }, cancelBtn, saveBtn)
            )
        )

        const close = () => overlay.remove()

        closeBtn.addEventListener('click', close)
        cancelBtn.addEventListener('click', close)
        overlay.addEventListener('click', e => { if (e.target === overlay) close() })

        saveBtn.addEventListener('click', () => {
            try {
                const data = readForm(form)
                if (!data.title) { showToast('⚠️ El título es obligatorio'); return }
                if (isEdit) {
                    onSave({ ...promoData, ...data })
                    showToast(`✅ "${data.title}" actualizada`)
                } else {
                    onSave({ id: nextId(promos), ...data })
                    showToast(`✅ "${data.title}" creada`)
                }
                close()
            } catch (err) {
                showToast(`❌ Error al guardar: ${err.message}`, 5000)
                console.error('[AdminPromos] saveBtn error:', err)
            }
        })

        document.body.appendChild(overlay)
    }

    function buildRow(p) {
        const tagColor = TAG_COLORS[p.tag] || '#f1f5f9'

        const toggle = h('label', { className: 'adm-toggle' },
            h('input', { type: 'checkbox', checked: p.enabled !== false }),
            h('span',  { className: 'adm-toggle__slider' })
        )
        toggle.querySelector('input').addEventListener('change', e => {
            onToggle(p.id, e.target.checked)
        })

        const editBtn = h('button', { className: 'adm-btn adm-btn--ghost adm-btn--sm', type: 'button' }, '✏️ Editar')
        const delBtn  = h('button', { className: 'adm-btn adm-btn--danger adm-btn--sm', type: 'button' }, '🗑')

        editBtn.addEventListener('click', () => openModal(p))
        delBtn.addEventListener('click', () => {
            if (confirm(`¿Eliminar la promo "${p.title}"?`)) {
                onDelete(p.id)
                showToast(`🗑 "${p.title}" eliminada`)
            }
        })

        return h('tr', {},
            h('td', {},
                h('span', {
                    style: `background:${tagColor};padding:.2rem .55rem;border-radius:99px;font-size:.75rem;font-weight:600`
                }, p.tag)
            ),
            h('td', {}, h('strong', {}, p.title)),
            h('td', {}, p.business || '—'),
            h('td', {}, p.responsable || '—'),
            h('td', {}, p.expires  || '—'),
            h('td', {}, toggle),
            h('td', {}, h('div', { className: 'adm-row-actions' }, editBtn, delBtn))
        )
    }

    const addBtn = h('button', { className: 'adm-btn adm-btn--primary', type: 'button' }, '➕ Nueva promo')
    addBtn.addEventListener('click', () => openModal(null))

    const tbody = h('tbody', {})
    promos.forEach(p => tbody.appendChild(buildRow(p)))

    if (!promos.length) {
        tbody.appendChild(
            h('tr', {},
                h('td', { colSpan: 7 },
                    h('div', { className: 'adm-empty' },
                        h('div', { className: 'adm-empty__icon' }, '🎁'),
                        h('div', { className: 'adm-empty__text' }, 'No hay promociones registradas')
                    )
                )
            )
        )
    }

    return h('div', {},
        h('div', { className: 'admin-panel' },
            h('div', { className: 'admin-panel__head' },
                h('div', {},
                    h('span', { className: 'admin-panel__title' }, '🎁 Promociones'),
                    h('span', { className: 'admin-panel__count' }, `${promos.length} total`)
                ),
                addBtn
            ),
            h('div', { style: 'overflow-x:auto' },
                h('table', { className: 'admin-table' },
                    h('thead', {},
                        h('tr', {},
                            h('th', {}, 'Tag'),
                            h('th', {}, 'Título'),
                            h('th', {}, 'Negocio'),
                            h('th', {}, 'Responsable'),
                            h('th', {}, 'Vence'),
                            h('th', {}, 'Habilitada'),
                            h('th', {}, 'Acciones'),
                        )
                    ),
                    tbody
                )
            )
        )
    )
})
